import { AlertTriangle, CheckCircle2, Loader2, Mail, Phone, RefreshCw, ShieldCheck, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalHeader from '../components/PortalHeader';
import PhoneNumberInput from '../components/auth/PhoneNumberInput';
import { storePhoneCountry } from '../components/auth/phoneCountryStorage';
import { userAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { clearAuthenticatedUser } from '../services/authStorage';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

const identityErrorMessage = (error, fallback) => {
  const code = error.response?.data?.error;
  if (code === 'PRIMARY_IDENTITY_REQUIRED') return 'Your Primary sign-in identity cannot be removed.';
  if (code === 'IDENTITY_TYPE_ALREADY_BOUND') return 'Remove the current Secondary identity before binding another one.';
  if (code === 'IDENTITY_ALREADY_IN_USE') return 'This contact already belongs to another account.';
  if (code === 'INVALID_CODE') return 'The verification code is incorrect.';
  if (code === 'CHALLENGE_EXPIRED' || code === 'CHALLENGE_LOCKED') return 'This code can no longer be used. Request a new one.';
  return error.response?.data?.detail || fallback;
};

export default function Profile({ embedded = false }) {
  const navigate = useNavigate();
  const [identities, setIdentities] = useState({});
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [flow, setFlow] = useState(null);
  const [emailValue, setEmailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState(null);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [removal, setRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [accountDeletionOpen, setAccountDeletionOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [accountDeletionError, setAccountDeletionError] = useState('');

  const loadIdentities = useCallback(async () => {
    const response = await userAPI.getIdentities();
    setIdentities(responseData(response) || {});
    return responseData(response) || {};
  }, []);

  useEffect(() => {
    let active = true;
    loadIdentities().catch(() => {
      if (active) setPageError('Could not load your verified contacts. Try again.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [loadIdentities]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = window.setInterval(() => setCountdown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [countdown]);

  const openFlow = (type) => {
    setFlow({ type, challengeId: '', maskedDestination: '' });
    setEmailValue('');
    setPhoneValue(null);
    setPhoneCountry(null);
    setCode('');
    setError('');
    setStatus('idle');
    setCountdown(0);
  };

  const closeFlow = () => {
    setFlow(null);
    setEmailValue('');
    setPhoneValue(null);
    setPhoneCountry(null);
    setCode('');
    setError('');
    setStatus('idle');
    setCountdown(0);
  };

  const sendCode = async () => {
    const value = flow.type === 'email' ? emailValue.trim() : phoneValue;
    if (flow.type === 'email' ? !EMAIL_PATTERN.test(value || '') : !PHONE_PATTERN.test(value || '')) {
      setError(flow.type === 'email'
        ? 'Enter a valid email address.'
        : 'Select a country and enter a valid phone number.');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const response = await userAPI.sendIdentityVerificationCode(flow.type, value);
      setFlow((current) => ({
        ...current,
        challengeId: responseData(response).challenge_id,
        maskedDestination: responseData(response).masked_destination || value,
      }));
      setCountdown(Number(responseData(response).resend_after_seconds) || 60);
      setStatus('code');
    } catch (requestError) {
      setError(identityErrorMessage(requestError, 'Could not send a verification code. Try again.'));
      setStatus('idle');
    }
  };

  const verifyIdentity = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit verification code.');
      return;
    }
    setStatus('verifying');
    setError('');
    try {
      await userAPI.linkIdentity(flow.challengeId, code);
      const updated = await loadIdentities();
      const verifiedValue = updated[flow.type]?.value;
      if (verifiedValue) {
        localStorage.setItem(flow.type === 'email' ? 'user_email' : 'user_phone', verifiedValue);
      }
      if (flow.type === 'phone' && phoneCountry) {
        storePhoneCountry(localStorage, phoneCountry);
      }
      closeFlow();
    } catch (requestError) {
      setError(identityErrorMessage(requestError, 'Could not verify this contact. Try again.'));
      setStatus('code');
    }
  };

  const removeIdentity = async () => {
    if (!removal || removing) return;
    setRemoving(true);
    setRemoveError('');
    try {
      await userAPI.removeIdentity(removal.type);
      await loadIdentities();
      localStorage.removeItem(removal.type === 'email' ? 'user_email' : 'user_phone');
      setRemoval(null);
    } catch (requestError) {
      setRemoveError(identityErrorMessage(requestError, 'Could not remove this identity. Try again.'));
    } finally {
      setRemoving(false);
    }
  };

  const deleteAccount = async () => {
    if (deletingAccount) return;
    setDeletingAccount(true);
    setAccountDeletionError('');
    try {
      await userAPI.deleteAccount();
      clearAuthenticatedUser({ includeSession: true });
      navigate('/login', { replace: true });
    } catch (requestError) {
      setAccountDeletionError(requestError.response?.data?.detail || 'Could not delete the account. Try again.');
      setDeletingAccount(false);
    }
  };

  const renderEditor = (type) => {
    if (flow?.type !== type) return null;
    const isCodeStep = Boolean(flow.challengeId);
    const busy = status === 'sending' || status === 'verifying';
    return (
      <div className="mt-5 border-t border-slate-200 pt-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{isCodeStep ? `Verify ${type}` : `Bind ${type}`}</h3>
            {isCodeStep ? <p className="mt-1 text-sm text-slate-500">Code sent to {flow.maskedDestination}.</p> : null}
          </div>
          <button type="button" onClick={closeFlow} disabled={busy}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label={`Cancel ${type} verification`}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {!isCodeStep ? (
          <div className="mt-4 space-y-3">
            {type === 'email' ? (
              <label className="block">
                <span className="sr-only">Email address</span>
                <input id="profile-email-contact" type="email" autoComplete="email" value={emailValue}
                  onChange={(event) => setEmailValue(event.target.value)} disabled={busy}
                  placeholder="name@example.com" className="input-field h-12" />
              </label>
            ) : (
              <PhoneNumberInput disabled={busy} onChange={setPhoneValue} onCountryChange={setPhoneCountry}
                invalid={Boolean(error) && !phoneValue} />
            )}
            <div className="flex justify-end">
              <button type="button" onClick={sendCode} disabled={busy} className="btn-primary h-12 w-full sm:w-auto sm:min-w-32">
                {status === 'sending' ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Send code'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="min-w-0 flex-1">
                <span className="sr-only">Verification code</span>
                <input id={`profile-${type}-code`} type="text" inputMode="numeric" autoComplete="one-time-code"
                  maxLength={6} value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={busy} placeholder="6-digit code" className="input-field h-12 tracking-[0.25em]" />
              </label>
              <button type="button" onClick={verifyIdentity} disabled={busy} className="btn-primary h-12 shrink-0 sm:min-w-36">
                {status === 'verifying' ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : `Verify ${type}`}
              </button>
            </div>
            <button type="button" onClick={sendCode} disabled={busy || countdown > 0}
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-cyan-700 disabled:text-slate-400">
              <RefreshCw className="h-4 w-4" />
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
            </button>
          </div>
        )}
        {error ? <p className="mt-3 text-sm text-red-700" role="alert">{error}</p> : null}
      </div>
    );
  };

  const renderIdentity = (type, Icon) => {
    const record = identities[type];
    const primary = Boolean(record?.is_primary);
    return (
      <section className="py-5 first:pt-0 last:pb-0" aria-labelledby={`${type}-identity-title`}>
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex min-w-0 items-start gap-3">
            <Icon className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 id={`${type}-identity-title`} className="text-sm font-medium capitalize text-slate-800">{type}</h2>
                {record ? <span className={`text-xs font-semibold ${primary ? 'text-cyan-800' : 'text-slate-500'}`}>{primary ? 'Primary' : 'Secondary'}</span> : null}
              </div>
              <p className="mt-1 break-all text-sm text-slate-600">{loading ? 'Loading...' : record?.value || `No verified ${type}`}</p>
              {record ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  {primary ? 'Verified registration identity' : 'Verified sign-in identity'}
                </p>
              ) : null}
            </div>
          </div>
          {!loading && !record && flow?.type !== type ? (
            <button type="button" onClick={() => openFlow(type)} className="btn-secondary h-10 w-full shrink-0 py-2 text-sm sm:w-auto">
              {type === 'email' ? 'Bind email' : 'Bind phone'}
            </button>
          ) : null}
          {!loading && record && !primary ? (
            <button type="button" onClick={() => { setRemoval({ type, value: record.value }); setRemoveError(''); }}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded border border-red-200 px-3 text-sm font-semibold text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />Remove
            </button>
          ) : null}
        </div>
        {renderEditor(type)}
      </section>
    );
  };

  const content = (
    <section id="profile" aria-labelledby="profile-title">
      <div className="pb-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-cyan-700" />
          <div>
            <h1 id="profile-title" className="text-lg font-semibold text-slate-950">Profile</h1>
            <p className="mt-1 text-sm text-slate-500">Verified contacts used to sign in to your account.</p>
          </div>
        </div>
      </div>
      {pageError ? <p className="mb-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">{pageError}</p> : null}
      <div
        className="ml-2 divide-y divide-slate-200 border-l border-slate-200 pl-4 sm:ml-9 sm:pl-5"
        data-testid="profile-identity-list"
      >
        {renderIdentity('phone', Phone)}
        {renderIdentity('email', Mail)}
      </div>
      <section className="mt-8 border-t border-red-200 pt-6" aria-labelledby="delete-account-title">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 id="delete-account-title" className="text-sm font-semibold text-slate-900">Delete account</h2>
            <p className="mt-1 max-w-xl text-sm text-slate-600">
              Remove all verified login identities. Your account, business, and transaction records are retained.
            </p>
          </div>
          <button type="button" onClick={() => { setAccountDeletionOpen(true); setAccountDeletionError(''); }}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded border border-red-200 px-3 text-sm font-semibold text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />Delete account
          </button>
        </div>
      </section>
      {removal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4" role="presentation">
          <div className="w-full max-w-md rounded border border-slate-200 bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="remove-identity-title">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div>
                <h2 id="remove-identity-title" className="text-base font-semibold text-slate-950">Remove Secondary {removal.type}?</h2>
                <p className="mt-2 break-all text-sm text-slate-600">{removal.value}</p>
                <p className="mt-2 text-sm text-slate-600">You will no longer be able to sign in with this contact.</p>
              </div>
            </div>
            {removeError ? <p className="mt-4 text-sm text-red-700" role="alert">{removeError}</p> : null}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setRemoval(null)} disabled={removing} className="btn-secondary h-11 sm:min-w-24">Cancel</button>
              <button type="button" onClick={removeIdentity} disabled={removing}
                className="inline-flex h-11 items-center justify-center rounded bg-red-700 px-4 text-sm font-semibold text-white hover:bg-red-800 disabled:bg-red-300 sm:min-w-32">
                {removing ? <Loader2 className="h-5 w-5 animate-spin" /> : `Remove ${removal.type}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {accountDeletionOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4" role="presentation">
          <div className="w-full max-w-md rounded border border-slate-200 bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="delete-account-dialog-title">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div>
                <h2 id="delete-account-dialog-title" className="text-base font-semibold text-slate-950">Delete account login access?</h2>
                <p className="mt-2 text-sm text-slate-600">
                  This removes all verified login identities. Your business and transaction records are retained.
                </p>
              </div>
            </div>
            {accountDeletionError ? <p className="mt-4 text-sm text-red-700" role="alert">{accountDeletionError}</p> : null}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setAccountDeletionOpen(false)} disabled={deletingAccount} className="btn-secondary h-11 sm:min-w-24">Cancel</button>
              <button type="button" onClick={deleteAccount} disabled={deletingAccount}
                className="inline-flex h-11 items-center justify-center rounded bg-red-700 px-4 text-sm font-semibold text-white hover:bg-red-800 disabled:bg-red-300 sm:min-w-32">
                {deletingAccount ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );

  if (embedded) return content;

  const accountLabel = identities.email?.value || identities.phone?.value || '';
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PortalHeader user={{ email: accountLabel }} onBack={() => navigate('/')} backLabel="Back to home" showHome />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="border border-slate-200 bg-white px-7 py-6">{content}</div>
      </main>
    </div>
  );
}
